import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Button, Container, Group, Paper, Select, Stack, Table, Text, TextInput, Title } from "@mantine/core"
import { modals } from "@mantine/modals"
import { useState } from "react"
import { TableSkeleton } from "../../../../components/PageSkeletons"
import type { IProps } from "./interfaces"

export const MembersView = ({
  members,
  invites,
  currentUserId,
  canManage,
  isLoading,
  inviting,
  onInvite,
  onRevoke,
  onRemove,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<"member" | "admin">("member")

  const handleInvite = (e: React.FormEvent): void => {
    e.preventDefault()
    onInvite(email, role)
    setEmail("")
    setRole("member")
  }

  // Revoking an invitation is destructive (the link stops working), so confirm
  // first — consistent with the member-removal confirm pattern.
  const confirmRevoke = (invitationId: string, invitedEmail: string): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Revoke invitation for ${invitedEmail}?`),
      children: <Text size="sm">{i18n._(msg`Their invitation link will stop working immediately.`)}</Text>,
      labels: { confirm: i18n._(msg`Revoke`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => onRevoke(invitationId),
    })
  }

  return (
    <Container size="md" py="md">
      <Title order={2} mb="md">
        <Trans>Members</Trans>
      </Title>

      {canManage ? (
        <Paper withBorder p="lg" mb="md">
          <form onSubmit={handleInvite}>
            <Group align="flex-end" gap="sm">
              <TextInput
                style={{ flex: 1 }}
                label={i18n._(msg`Invite a teammate`)}
                placeholder="teammate@company.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
              />
              <Select
                label={i18n._(msg`Role`)}
                value={role}
                onChange={(v) => {
                  if (v === "member" || v === "admin") setRole(v)
                }}
                data={[
                  { value: "member", label: i18n._(msg`Member`) },
                  { value: "admin", label: i18n._(msg`Admin`) },
                ]}
                w={140}
              />
              <Button type="submit" loading={inviting}>
                <Trans>Send invite</Trans>
              </Button>
            </Group>
          </form>
        </Paper>
      ) : null}

      {isLoading ? (
        <TableSkeleton cols={4} />
      ) : (
        <Paper withBorder p="lg">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Trans>Name</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Email</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Role</Trans>
                </Table.Th>
                {canManage ? <Table.Th /> : null}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {members.map((m) => (
                <Table.Tr key={m.id}>
                  <Table.Td>{m.name || "—"}</Table.Td>
                  <Table.Td>{m.email}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">
                      {m.role}
                    </Badge>
                  </Table.Td>
                  {canManage ? (
                    <Table.Td ta="right">
                      {m.userId !== currentUserId && m.role !== "owner" ? (
                        <Button size="compact-xs" variant="subtle" color="danger" onClick={() => onRemove(m)}>
                          <Trans>Remove</Trans>
                        </Button>
                      ) : null}
                    </Table.Td>
                  ) : null}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {invites.length > 0 ? (
            <Stack gap="xs" mt="lg">
              <Text size="sm" fw={600} c="dimmed">
                <Trans>Pending invitations</Trans>
              </Text>
              {invites.map((inv) => (
                <Group key={inv.id} justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text size="sm" truncate>
                      {inv.email}
                    </Text>
                    <Badge variant="light" size="sm">
                      {inv.role}
                    </Badge>
                  </Group>
                  {canManage ? (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="danger"
                      onClick={() => confirmRevoke(inv.id, inv.email)}
                    >
                      <Trans>Revoke</Trans>
                    </Button>
                  ) : null}
                </Group>
              ))}
            </Stack>
          ) : null}
        </Paper>
      )}
    </Container>
  )
}
